import { useState, useCallback, useMemo } from 'react';
import { dummyStaff, dummyShifts, dummyLeaveRequests } from '../Data/DummyData';

export const useRoster = () => {
  const [staff, setStaff] = useState(dummyStaff);
  const [shifts] = useState(dummyShifts);
  const [leaveRequests, setLeaveRequests] = useState(dummyLeaveRequests);
  const [rosterEntries, setRosterEntries] = useState([]);
  const [currentView, setCurrentView] = useState({
    type: 'day-shift-staff',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 27 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  // Get active staff (not benched)
  const activeStaff = useMemo(() => 
    staff.filter(s => s.status === 'active'), [staff]
  );

  // Get benched staff (available for emergency)
  const benchedStaff = useMemo(() => 
    staff.filter(s => s.status === 'benched'), [staff]
  );

  // Generate date range for current view
  const dateRange = useMemo(() => {
    const dates = [];
    const start = new Date(currentView.startDate);
    const end = new Date(currentView.endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split('T')[0]);
    }
    return dates;
  }, [currentView.startDate, currentView.endDate]);

  // Check if staff is on leave for a specific date
  const isStaffOnLeave = useCallback((staffId, date) => {
    return leaveRequests.some(leave => 
      leave.staffId === staffId &&
      leave.status === 'approved' &&
      date >= leave.startDate &&
      date <= leave.endDate
    );
  }, [leaveRequests]);

  // Add staff to shift
  const assignStaffToShift = useCallback((staffId, shiftId, date) => {
    if (isStaffOnLeave(staffId, date)) {
      return false; // Cannot assign staff on leave
    }

    const newEntry = {
      id: `${date}-${shiftId}-${staffId}`,
      date,
      shiftId,
      staffId,
      confirmed: true,
    };

    setRosterEntries(prev => {
      // Remove any existing entry for this staff/date/shift combo
      const filtered = prev.filter(entry => 
        !(entry.date === date && entry.shiftId === shiftId && entry.staffId === staffId)
      );
      return [...filtered, newEntry];
    });
    return true;
  }, [isStaffOnLeave]);

  // Remove staff from shift
  const removeStaffFromShift = useCallback((staffId, shiftId, date) => {
    setRosterEntries(prev => 
      prev.filter(entry => 
        !(entry.date === date && entry.shiftId === shiftId && entry.staffId === staffId)
      )
    );
  }, []);

  // Move staff from bench to active
  const activateStaff = useCallback((staffId) => {
    setStaff(prev => prev.map(s => 
      s.id === staffId ? { ...s, status: 'active' } : s
    ));
  }, []);

  // Move staff to bench
  const benchStaff = useCallback((staffId) => {
    setStaff(prev => prev.map(s => 
      s.id === staffId ? { ...s, status: 'benched' } : s
    ));
  }, []);

  // Approve leave request
  const approveLeaveRequest = useCallback((leaveId) => {
    setLeaveRequests(prev => prev.map(leave => 
      leave.id === leaveId ? { ...leave, status: 'approved' } : leave
    ));

    // Remove affected roster entries
    const leaveRequest = leaveRequests.find(l => l.id === leaveId);
    if (leaveRequest) {
      setRosterEntries(prev => prev.filter(entry => {
        if (entry.staffId !== leaveRequest.staffId) return true;
        return entry.date < leaveRequest.startDate || entry.date > leaveRequest.endDate;
      }));
    }
  }, [leaveRequests]);

  // Generate 28-day roster
  const generateRoster = useCallback((type) => {
    const startDate = new Date(currentView.startDate);
    const entries = [];

    if (type === 'weekly') {
      // Generate 1 week pattern and repeat 4 times
      const weekPattern = [];
      
      // Simple pattern: rotate staff through shifts
      for (let day = 0; day < 7; day++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + day);
        const dateStr = date.toISOString().split('T')[0];

        shifts.forEach((shift, shiftIndex) => {
          const availableStaff = activeStaff.filter(s => !isStaffOnLeave(s.id, dateStr));
          const staffNeeded = Math.min(shift.requiredStaff, availableStaff.length);
          
          for (let i = 0; i < staffNeeded; i++) {
            const staffIndex = (day * shifts.length + shiftIndex + i) % availableStaff.length;
            const staff = availableStaff[staffIndex];
            
            weekPattern.push({
              id: `${dateStr}-${shift.id}-${staff.id}`,
              date: dateStr,
              shiftId: shift.id,
              staffId: staff.id,
              confirmed: true,
            });
          }
        });
      }

      // Repeat pattern for 4 weeks
      for (let week = 0; week < 4; week++) {
        weekPattern.forEach(entry => {
          const newDate = new Date(entry.date);
          newDate.setDate(newDate.getDate() + (week * 7));
          const newDateStr = newDate.toISOString().split('T')[0];
          
          if (!isStaffOnLeave(entry.staffId, newDateStr)) {
            entries.push({
              ...entry,
              id: `${newDateStr}-${entry.shiftId}-${entry.staffId}`,
              date: newDateStr,
            });
          }
        });
      }
    } else {
      // Continuous 28-day generation
      for (let day = 0; day < 28; day++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + day);
        const dateStr = date.toISOString().split('T')[0];

        shifts.forEach((shift, shiftIndex) => {
          const availableStaff = activeStaff.filter(s => !isStaffOnLeave(s.id, dateStr));
          const staffNeeded = Math.min(shift.requiredStaff, availableStaff.length);
          
          for (let i = 0; i < staffNeeded; i++) {
            const staffIndex = (day * shifts.length + shiftIndex + i) % availableStaff.length;
            const staff = availableStaff[staffIndex];
            
            entries.push({
              id: `${dateStr}-${shift.id}-${staff.id}`,
              date: dateStr,
              shiftId: shift.id,
              staffId: staff.id,
              confirmed: true,
            });
          }
        });
      }
    }

    setRosterEntries(entries);
  }, [currentView.startDate, activeStaff, shifts, isStaffOnLeave]);

  return {
    staff,
    activeStaff,
    benchedStaff,
    shifts,
    leaveRequests,
    rosterEntries,
    currentView,
    dateRange,
    setCurrentView,
    assignStaffToShift,
    removeStaffFromShift,
    activateStaff,
    benchStaff,
    approveLeaveRequest,
    generateRoster,
    isStaffOnLeave,
  };
};